import { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { FormikProps, useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { Typography } from '@kep-ui-kit/ui-components';
import { withErrorHandling } from '@selfserve/react-hocs';
import {
  StyledLogo,
  StyledV2PageWrapper,
  StyledV2LogoRow,
  StyledV2PageBackground,
  StyledV2GlassCard,
} from '@selfserve/react-ui';
import {
  apiStatuses,
  getRecaptchaToken,
  routesPaths,
  passwordNumberRegex,
  passwordSpecialCharRegex,
  passwordUpperCaseRegex,
  passwordLowerCaseRegex,
} from '@selfserve/react-utils';
import { SetPasswordFormV2 } from '../ui/set-password-form-v2';
import { useSetPasswordRequest } from '../data';

const StyledGlassCard = styled(StyledV2GlassCard)(({ theme }: { theme?: any }) => ({
  maxWidth: '600px',
  gap: theme.spacing(3),
}));

const validationSchema = (t: TFunction) =>
  Yup.object().shape({
    password: Yup.mixed().required(t('common.validations.password-required')),
    confirmPassword: Yup.string().when('password', {
      is: (password: string) => !!password,
      then: Yup.string()
        .required(t('common.validations.confirm-password-required'))
        .oneOf(
          [Yup.ref('password')],
          t('common.validations.confirm-password-match')
        ),
    }),
    passwordErrors: Yup.object().when('password', (password) => {
      return Yup.object().shape({
        minLengthErr: Yup.mixed().test({
          test: () => !!(password && password.length >= 8),
        }),
        oneNumberErr: Yup.mixed().test({
          test: () => !!(password && passwordNumberRegex.test(`${password}`)),
        }),
        specialCharErr: Yup.mixed().test({
          test: () => !!(password && passwordSpecialCharRegex.test(`${password}`)),
        }),
        upperAndLowerCaseErr: Yup.mixed().test({
          test: () =>
            !!(
              password &&
              passwordUpperCaseRegex.test(`${password}`) &&
              passwordLowerCaseRegex.test(`${password}`)
            ),
        }),
      });
    }),
  });

export interface FormValues {
  password: string;
  passwordErrors: {
    minLengthErr: boolean;
    oneNumberErr: boolean;
    specialCharErr: boolean;
    upperAndLowerCaseErr: boolean;
  };
  confirmPassword: string;
}

export const SetPasswordV2 = withErrorHandling(({ openErrorModal }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isInProccess, setIsInProccess] = useState(false);
  const { token: passwordHash } = useParams();
  const {
    data: setPasswordResponse,
    mutateAsync: handleSetPasswordRequest,
    isLoading,
  } = useSetPasswordRequest();
  const formik: FormikProps<FormValues> = useFormik<FormValues>({
    validateOnBlur: false,
    validateOnChange: true,
    initialValues: {
      password: '',
      confirmPassword: '',
      passwordErrors: {
        minLengthErr: false,
        oneNumberErr: false,
        specialCharErr: false,
        upperAndLowerCaseErr: false,
      },
    },
    validationSchema: useMemo(() => validationSchema(t), [t]),
    onSubmit: ({ password: newPassword }) => {
      setIsInProccess(true);
      getRecaptchaToken()
        .then((recaptchaToken: string) => {
          return handleSetPasswordRequest({
            recaptchaToken,
            passwordHash,
            newPassword,
          }).catch(() => {
            setIsInProccess(false);
            openErrorModal();
          });
        })
        .catch(() => {
          setIsInProccess(false);
          openErrorModal();
        });
    },
  });
  const { values, touched, setFieldTouched } = formik;

  useEffect(() => {
    const { data, status } = setPasswordResponse || {};
    if (status) {
      setIsInProccess(false);
      if (status === apiStatuses.BAD_REQUEST) {
        openErrorModal({
          text: data?.message_en,
          title: t('common.cant-reset-password'),
        });
      }
      if (status === apiStatuses.CREATED) {
        navigate(routesPaths.HOMEPAGE);
      }
    }
  }, [setPasswordResponse]);

  useEffect(() => {
    if (values.password && !touched.password) {
      setFieldTouched('password', true);
    }
  }, [values.password]);

  return (
    <StyledV2PageWrapper>
      <StyledV2LogoRow>
        <StyledLogo alt="Kaltura" />
      </StyledV2LogoRow>
      <StyledV2PageBackground centered className="container">
        <StyledGlassCard>
          <Typography variant="h2" textAlign="center">
            {t('common.set-password')}
          </Typography>
          <SetPasswordFormV2
            formik={formik}
            isLoading={isLoading || isInProccess}
          />
        </StyledGlassCard>
      </StyledV2PageBackground>
    </StyledV2PageWrapper>
  );
});
